import React, { createContext, useCallback, useContext, useMemo, useState, useEffect, ReactNode } from 'react';
import type { UiConceptId } from './conceptTypes';
import { DEFAULT_UI_CONCEPT_ID, isUiConceptId } from './conceptDesignRegistry';

const STORAGE_KEY = 'mmec_ui_concept';
const URL_PARAM = 'uiConcept';

interface ConceptExperienceContextValue {
  conceptId: UiConceptId;
  setConceptId: (id: UiConceptId) => void;
}

export const ConceptExperienceContext = createContext<ConceptExperienceContextValue | undefined>(undefined);

function readInitialConcept(): UiConceptId {
  if (typeof window === 'undefined') return DEFAULT_UI_CONCEPT_ID;
  try {
    const q = new URLSearchParams(window.location.search).get(URL_PARAM);
    if (q && isUiConceptId(q)) return q;
  } catch {
    /* ignore */
  }
  const s = localStorage.getItem(STORAGE_KEY);
  if (s && isUiConceptId(s)) return s;
  return DEFAULT_UI_CONCEPT_ID;
}

export const ConceptExperienceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [conceptId, setConceptIdState] = useState<UiConceptId>(readInitialConcept);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, conceptId);
    try {
      const url = new URL(window.location.href);
      url.searchParams.set(URL_PARAM, conceptId);
      window.history.replaceState({}, '', url.toString());
    } catch {
      /* ignore */
    }
  }, [conceptId]);

  const setConceptId = useCallback((id: UiConceptId) => {
    setConceptIdState(id);
  }, []);

  const value = useMemo(() => ({ conceptId, setConceptId }), [conceptId, setConceptId]);

  return <ConceptExperienceContext.Provider value={value}>{children}</ConceptExperienceContext.Provider>;
};

export function useUiConcept(): ConceptExperienceContextValue {
  const ctx = useContext(ConceptExperienceContext);
  if (!ctx) throw new Error('useUiConcept must be used within ConceptExperienceProvider');
  return ctx;
}
